import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Puzzle, CheckCircle2, Clock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { listPlugins, createPlugin } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Plugins catalog page (iter-40, iter-44 scroll/compact, iter-45 CRUD).
 *
 * Tapping any enabled plugin navigates to its detail page where the user
 * can manage their saved prompts (and the superadmin can edit the plugin
 * itself). Disabled plugins render greyed out and are not clickable.
 *
 * Superadmin: extra "+ New plugin" button at the top.
 */
export default function PluginsPage() {
    const [plugins, setPlugins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const isSuperadmin = user?.role === "superadmin";

    const load = async () => {
        setLoading(true);
        try {
            setPlugins(await listPlugins());
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Couldn't load plugins",
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleOpen = (plugin) => {
        // iter-47: superadmin can always open (to re-enable a disabled plugin).
        if (plugin.status !== "active" && !isSuperadmin) return;
        navigate(`/plugins/${plugin.id}`);
    };

    return (
        <div
            className="h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-slate-100 flex flex-col"
            data-testid="plugins-page"
        >
            <main className="flex-1 px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto w-full">
                <header className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
                    <div>
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#4f6fe8]">Estimating</p>
                        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Plugins</h1>
                    </div>
                    {isSuperadmin && (
                        <Button
                            onClick={() => setCreateOpen(true)}
                            data-testid="plugins-create-btn"
                            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1a3fd1]/85"
                        >
                            <Plus className="h-4 w-4" />
                            New plugin
                        </Button>
                    )}
                </header>

                {loading ? (
                    <div
                        className="text-sm text-slate-500"
                        data-testid="plugins-loading"
                    >
                        Loading catalog…
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 pb-24">
                        {plugins.map((p) => (
                            <PluginCard
                                key={p.id}
                                plugin={p}
                                onActivate={handleOpen}
                                canOpenDisabled={isSuperadmin}
                            />
                        ))}
                    </div>
                )}
            </main>

            {isSuperadmin && (
                <CreatePluginDialog
                    open={createOpen}
                    onOpenChange={setCreateOpen}
                    onCreated={load}
                />
            )}
        </div>
    );
}

function PluginCard({
    plugin, onActivate, canOpenDisabled = false }) {
    const [brokenIcons, setBrokenIcons] = useState({});
    const isActive = plugin.status === "active";
    const clickable = isActive || canOpenDisabled;
    return (
        <button
            type="button"
            onClick={() => onActivate(plugin)}
            disabled={!clickable}
            data-testid={`plugin-card-${plugin.id}`}
            data-status={plugin.status}
            className={`group relative overflow-hidden rounded-lg border text-left transition-colors flex flex-col ${
                isActive
                    ? "border-white/15 bg-[#111] hover:border-[#1a3fd1]/60 active:border-[#1a3fd1]/50 cursor-pointer"
                    : clickable
                        ? "border-amber-500/40 bg-[#0a0a0a] opacity-70 grayscale hover:opacity-90 cursor-pointer"
                        : "border-white/10 bg-[#0a0a0a] opacity-40 grayscale cursor-not-allowed"
            }`}
        >
            <div className="relative bg-white/[0.03] overflow-hidden aspect-square w-full">
                {plugin.icon_type === "video" && plugin.icon ? (
                    <video
                        src={plugin.icon}
                        poster={plugin.poster || undefined}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                        data-testid={`plugin-video-${plugin.id}`}
                    />
                ) : plugin.icon && !brokenIcons[plugin.id] ? (
                    <img
                        src={plugin.icon}
                        alt={plugin.name}
                        onError={() => setBrokenIcons((prev) => ({ ...prev, [plugin.id]: true }))}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div
                        className="w-full h-full flex items-center justify-center font-black text-3xl tracking-tight"
                        style={{
                            color: "#0a1128",
                            backgroundColor: plugin.accent || "#334155",
                        }}
                    >
                        {(plugin.name || "?")[0]}
                    </div>
                )}
                {!isActive && (
                    <div className="absolute top-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-slate-900/80 border border-slate-700 px-1.5 py-0.5 text-[8px] uppercase tracking-wider font-bold text-amber-300">
                        <Clock className="w-2 h-2" />
                        Off
                    </div>
                )}
                {isActive && (
                    <div className="absolute top-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/50 px-1.5 py-0.5 text-[8px] uppercase tracking-wider font-bold text-emerald-300">
                        <CheckCircle2 className="w-2 h-2" />
                        Active
                    </div>
                )}
            </div>
            <div className="p-2 sm:p-2.5">
                <h3
                    className="text-[13px] sm:text-sm font-bold tracking-tight text-white leading-tight"
                    style={isActive ? { color: plugin.accent || "#ffffff" } : {}}
                >
                    {plugin.name}
                </h3>
                <p className="mt-0.5 text-[10px] sm:text-[11px] text-slate-400 leading-snug line-clamp-2">
                    {plugin.description}
                </p>
            </div>
        </button>
    );
}

function CreatePluginDialog({ open, onOpenChange, onCreated }) {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: "",
        description: "",
        accent: "#1a3fd1",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open)
            setForm({ name: "", description: "", accent: "#1a3fd1" });
    }, [open]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Plugin name is required.");
            return;
        }
        setSaving(true);
        try {
            const plugin = await createPlugin({
                name: form.name.trim(),
                description: form.description.trim(),
                accent: form.accent,
            });
            toast.success("Plugin created");
            onOpenChange(false);
            onCreated();
            // Jump straight into its detail page so the user can upload
            // an icon + add their first prompt. (Ported: in-module router
            // navigation — window.location would leave the host app.)
            navigate(`/plugins/${plugin.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#111] border-white/15 text-slate-100 sm:max-w-md"
                data-testid="plugins-create-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white">
                        New plugin
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                        Give it a name and accent — you can upload an icon
                        and add prompts on the next screen.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                            Name
                        </label>
                        <Input
                            autoFocus
                            value={form.name}
                            onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                            }
                            placeholder="e.g. Plumbing"
                            data-testid="plugins-create-name"
                            className="mt-1 bg-[#0a0a0a] border-white/15 text-white"
                        />
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                            Description
                        </label>
                        <Textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) =>
                                setForm({
                                    ...form,
                                    description: e.target.value,
                                })
                            }
                            placeholder="Short description of the trade scope"
                            data-testid="plugins-create-description"
                            className="mt-1 bg-[#0a0a0a] border-white/15 text-white text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                            Accent
                        </label>
                        <input
                            type="color"
                            value={form.accent}
                            onChange={(e) =>
                                setForm({ ...form, accent: e.target.value })
                            }
                            data-testid="plugins-create-accent"
                            className="w-10 h-8 rounded border border-white/15 bg-[#0a0a0a] cursor-pointer"
                        />
                        <span className="text-[11px] text-slate-500 font-mono">
                            {form.accent}
                        </span>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="plugins-create-submit"
                            className="bg-emerald-500 hover:bg-emerald-600"
                        >
                            Create
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
